import React, { FC, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import {
  match as RouterMatch,
  RouteComponentProps,
  StaticContext,
  useHistory,
} from 'react-router';
import { ErrorPage, Button } from '@clearcover/atlas';
import { Asset } from 'Shared/components/Asset';
import { PolicyBlockModal } from 'BlockList/components/PolicyBlockModal';
import { manageBlockListPolicy } from 'Core/authPolicies';
import {
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyResponseTypeV2,
  PolicyDocumentType,
} from '@clearcover/core-app-types';

import { AchHeaderBar, PageHeaderBar } from 'Shared/components';
import { useToast } from 'Shared/components/Toast';
import { useResourceEffectiveGrants } from 'Shared/hooks';
import { combineStrings, isPresent } from 'Shared/utils';
import { useAuth0 } from 'Auth';
import {
  PolicyDetailsDisplay,
  StaggeredAnimatedComponent,
  StaggeredAnimationContainer,
  useDocuments,
} from 'Policies/components';
import { PolicyDetailsHeader } from 'Policies/components/PolicyDetailsHeader';
import { SuccessModal } from 'Policies/components/PolicySummary';
import { PolicyContext } from 'Policies/utils';
import styles from './styles.module.css';
import { useNewApi, useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { DocumentsContext } from 'Policies/utils/DocumentsContext';
import { useVehicleInspectionEligibility } from 'Shared/services/Api/useVehicleInspectionEligibility';
import { DriverOptionsResponse } from 'Policies/components/PolicyDriversSection/DriverForm';

export const createGetPolicyPath = (
  match: RouterMatch<{ id: string; termNumber?: string }>
) => {
  if (isPresent(match.params.termNumber)) {
    return `/policies/${match.params.id}/term/${match.params.termNumber}`;
  }
  return `/policies/${match.params.id}`;
};

export const PolicyDetails: FC<
  RouteComponentProps<
    {
      id: string;
      termNumber?: string;
    },
    StaticContext,
    {
      updatePolicySuccess?: boolean;
    }
  >
> = ({ match, location }) => {
  const [{ data: policy, loading, error, refetch: fetchPolicy }] =
    useNewApiV2<GetPolicyResponseTypeV2>(
      { url: createGetPolicyPath(match) },
      false,
      false
    );

  const [draftList, setDraftList] = useState<
    GetPolicyResponseTypeV2['draftList']
  >([]);

  const [driverOptions, setDriverOptions] =
    useState<DriverOptionsResponse | null>(null);

  useEffect(() => {
    setDraftList(policy?.draftList ?? []);
  }, [policy?.draftList]);

  const { user } = useAuth0();
  const { toast } = useToast();

  const [addToBlocklistOpen, setAddToBlocklistOpen] = useState(false);

  const grants = user?.effectiveGrants;
  const policyNumber = policy?.policyDetails?.policyNumber;
  const canBlock = !!policyNumber && !!grants && manageBlockListPolicy(grants);

  const history = useHistory();
  const { loading: resourceEffectiveGrantsLoading, resourceEffectiveGrants } =
    useResourceEffectiveGrants(policy?.policyDetails.policyId);

  const [
    {
      data: documentsData,
      loading: documentsLoading,
      error: documentsError,
      refetch: fetchDocuments,
    },
  ] = useNewApi<{
    documents: Array<PolicyDocumentType>;
  }>(
    {
      url: `/policies/${match.params.id}/documentsList/${
        match.params.termNumber ?? ''
      }`,
    },
    false,
    false
  );

  const [
    {
      loading: billingLoading,
      data: billingInfoResponse,
      refetch: fetchBillingData,
      error: billingError,
    },
  ] = useNewApiV2<GetPaymentsSummaryByTermNumberResponse>(
    {
      url: `/payments/${match.params.id}/summary/${
        match.params.termNumber ? '?termNumber=' + match.params.termNumber : ''
      }`,
    },
    false,
    false
  );

  const { downloadDocument, downloadingDocuments, idCard } = useDocuments(
    match.params.id,
    documentsData?.documents ?? []
  );

  const {
    data: vehicleInspectionEligibility,
    refetch: fetchVehicleInspectionEligibility,
  } = useVehicleInspectionEligibility({ policyId: match.params.id });

  const refetchHandler = useCallback(() => {
    fetchDocuments();
    fetchPolicy();
    fetchBillingData();
    fetchVehicleInspectionEligibility();
  }, [
    fetchDocuments,
    fetchPolicy,
    fetchBillingData,
    fetchVehicleInspectionEligibility,
  ]);

  if (error) {
    return (
      <ErrorPage
        key="error"
        variant="normal"
        iconType="info"
        errorMessage={`${error?.message}`}
      >
        <div className={styles.errorMessageContainer}>
          <h1>We're Sorry</h1>
          <div className={styles.errorMessage}>
            <span>
              We're unable to locate that policy. Please check the policy id and
            </span>
            <span>
              retry or contact your administrator if you believe this is an
              error
            </span>
          </div>
          <Button
            onClick={() => history.push('/policies')}
            block
            data-testid="go-to-policy-list"
            variant="secondary"
            size={'sm'}
          >
            View Policies
          </Button>
        </div>
      </ErrorPage>
    );
  }
  if (!policy && !loading && !resourceEffectiveGrantsLoading)
    return <h1>No data found for the policy by #{match.params.id}</h1>;

  const applicantName = policy
    ? combineStrings([
        policy.policyHolder.name.firstName,
        policy.policyHolder.name.lastName,
      ])
    : 'Clearcover Customer';

  const isEndorseablePolicy = policy
    ? policy.systemOfRecord === 'clearcover' &&
      !policy.disableEndorsements &&
      policy.policyDetails.termInfo.termStatus !== 'expired' &&
      policy.policyDetails.termInfo.termStatus !== 'cancelled' &&
      policy.policyDetails.termInfo.termStatusDetailsList.every((status) =>
        [
          'pending_renewal',
          'non_renewed_by_insured',
          'non_renewed_by_insurer',
          'renewed',
          'future_renewal',
        ].includes(status)
      )
    : false;

  const hasRegionalEvent = policy ? policy.regionalEvents.length > 0 : false;

  const canEndorseDuringRegionalEvents =
    !hasRegionalEvent || user?.partner?.name === 'clearcover';

  const hasProcessOnePaymentMethod =
    billingInfoResponse?.billingInfo.paymentMethods.some(
      (method) => method.paymentProcessor === 'PROCESS_ONE'
    ) || false;

  const isClearcoverAgent = user?.partner?.name === 'clearcover';

  const isOchoPolicy =
    policy?.producerInfo.agency === 'Ocho Insurance Solutions LLC';

  return (
    <>
      {isClearcoverAgent && isOchoPolicy && <AchHeaderBar />}
      <PolicyContext.Provider
        value={{
          details: {
            region: policy?.policyDetails.policyRegion || '',
            effectiveDate: policy?.policyDetails.effectivePeriod.startsAt || '',
            timeZone: policy?.policyDetails.policyTimeZone || 'America/Chicago',
            filingIdentifier: policy?.policyDetails.filingIdentifier || '',
            configDiscoveryParams:
              policy?.policyDetails.configDiscoveryParams || '',
            isPolicyDraft: false,
            isRenewalDraft: false,
            draftType: undefined,
            termId: policy?.policyDetails.termInfo.termId || '',
            mailingState: policy?.policyDetails.addresses.mailing.state || '',
            showDriverCfrs: policy?.policyDetails.showDriverCfrs || false,
            askForUserReportedLicenseStatus:
              policy?.policyDetails.askForUserReportedLicenseStatus || false,
            requiredAccessStatements: policy?.requiredAccessStatements || false,
            showEducation: policy?.policyDetails.showEducation ?? true,
            showOccupation: policy?.policyDetails.showOccupation ?? true,
            showMilitarySection:
              policy?.policyDetails.showMilitarySection ?? true,
            showStudentSection:
              policy?.policyDetails.showStudentSection ?? true,
            showRegisteredToApplicantOrSpouse:
              policy?.policyDetails.showRegisteredToApplicantOrSpouse ?? true,
            showExcludedDriverGender:
              policy?.policyDetails?.showExcludedDriverGender ?? false,
            showExcludedDriverMaritalStatus:
              policy?.policyDetails?.showExcludedDriverMaritalStatus ?? false,
            showPricingData: policy?.policyDetails?.showPricingData ?? true,
          },
          person: {
            name: applicantName,
          },
          flags: {
            renewalQuotesEnabled: false, // unused in this context
            isFailingUnderwriting: false, // unused in this context
            mandatedDownPay: policy?.mandatedDownPay ?? false,
          },
          canEndorseDuringRegionalEvents,
          isEndorseablePolicy,
          systemOfRecord: policy?.systemOfRecord || 'clearcover',
          resourceEffectiveGrants,
          refetchHandler,
          loading,
          productSegment:
            policy?.policyDetails?.productSegment || 'standardAuto',
          draftList,
          removeDraft: (draftId) =>
            setDraftList(draftList.filter((draft) => draft.id !== draftId)),
          setDriverOptions,
          driverOptions,
        }}
      >
        <DocumentsContext.Provider
          value={{
            loading: documentsLoading,
            error: documentsError,
            refetch: fetchDocuments,
            documents: documentsData?.documents ?? [],
            downloadDocument,
            downloadingDocuments,
          }}
        >
          <StaggeredAnimationContainer
            className={classnames(styles.container, {
              [styles.warningBanner]: hasRegionalEvent,
            })}
          >
            {hasRegionalEvent && (
              <PageHeaderBar className={styles.moratoriumBanner}>
                This customer's area is currently under a moratorium. Until the
                moratorium is lifted, no coverage changes can be made.
              </PageHeaderBar>
            )}
            {location.state?.updatePolicySuccess && (
              <SuccessModal changeType="policy" />
            )}

            <StaggeredAnimatedComponent>
              <PolicyDetailsHeader
                applicantName={applicantName}
                effectivePeriod={policy?.policyDetails.effectivePeriod}
                policyNumber={policy?.policyDetails.policyNumber}
                termInfo={policy?.policyDetails.termInfo}
                currentPolicyTermNumber={
                  policy?.policyDetails.termInfo.termNumber
                }
                termStatusDetailsList={
                  policy?.policyDetails.termInfo.termStatusDetailsList
                }
                draftList={policy?.draftList.filter(
                  (d) => d.type === 'mid_term'
                )}
                buttons={
                  <div className={styles.buttonRow}>
                    <Button
                      isSubmitting={downloadingDocuments.hasOwnProperty(
                        idCard?.id || ''
                      )}
                      variant={'secondary'}
                      onClick={downloadDocument.downloadIdCard}
                      disabled={idCard === undefined}
                    >
                      Download ID Cards
                    </Button>
                    {canBlock && (
                      <Button
                        variant="danger-secondary"
                        onClick={() => setAddToBlocklistOpen(true)}
                        data-testid="block-policy-button"
                      >
                        <span className={styles.blockButtonContent}>
                          <Asset variant="shieldOff" />
                          Block
                        </span>
                      </Button>
                    )}
                  </div>
                }
                renewalDraftId={draftList.find((d) => d.type === 'renewal')?.id}
                processOne={hasProcessOnePaymentMethod}
                billingInfoResponse={billingInfoResponse}
              />
            </StaggeredAnimatedComponent>

            {canBlock && policy && (
              <PolicyBlockModal
                open={addToBlocklistOpen}
                onDismiss={() => setAddToBlocklistOpen(false)}
                onSuccess={() => {
                  setAddToBlocklistOpen(false);
                  toast({
                    message: 'Policy attributes blocked',
                    type: 'success',
                    icon: 'shieldOff',
                  });
                }}
                policy={policy}
              />
            )}

            <PolicyDetailsDisplay
              policy={policy}
              billingInfo={{
                billingInfoResponse,
                loading: billingLoading,
                error: billingError,
                fetchBillingData,
              }}
              vehicleInspectionEligibility={vehicleInspectionEligibility}
            />
          </StaggeredAnimationContainer>
        </DocumentsContext.Provider>
      </PolicyContext.Provider>
    </>
  );
};
